import { createQRGenerator, composeCanvasWithFrame } from './qr';
import type { QRConfig } from '../store/useAppStore';
import { getFrameMetrics, getRoundedFramePathString } from './qr-helpers';
import { FONT_FAMILY } from './constants';

export const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => { URL.revokeObjectURL(url); }, 100);
};

export const exportPNG = async (content: string, config: QRConfig, width: number, logoFile: string | null, filename: string) => {
  const qr = createQRGenerator(width, content, config, logoFile, true);
  const canvas = await composeCanvasWithFrame(qr, width, config);
  return new Promise<void>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        triggerDownload(blob, filename);
        resolve();
      } else {
        reject(new Error('Canvas toBlob failed'));
      }
    }, 'image/png');
  });
};

export const exportSVG = async (content: string, config: QRConfig, logoFile: string | null, filename: string) => {
  const width = 1024;
  const qr = createQRGenerator(width, content, config, logoFile, false);
  const rawSvgData = await qr.getRawData('svg');
  if (!rawSvgData) throw new Error('Failed to generate SVG');
  const rawSvgStr = rawSvgData instanceof Blob ? await rawSvgData.text() : typeof rawSvgData === 'string' ? rawSvgData : String(rawSvgData);
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawSvgStr, 'image/svg+xml');
  const svgNode = doc.documentElement;
  
  if (config.frameStyle !== 'none') {
    const { frameHeight, radius, fontSize } = getFrameMetrics(width, config.frameStyle);
    const currentViewBox = svgNode.getAttribute('viewBox') ?? `0 0 ${width} ${width}`;
    const [x, y, w, h] = currentViewBox.split(' ').map(Number);
    svgNode.setAttribute('viewBox', `${x} ${y} ${w} ${h + frameHeight}`);
    svgNode.setAttribute('height', `${h + frameHeight}`);
    
    // Frame background
    const frameRect = doc.createElementNS('http://www.w3.org/2000/svg', 'rect');
    frameRect.setAttribute('x', '0');
    frameRect.setAttribute('y', `${width}`);
    frameRect.setAttribute('width', `${width}`);
    frameRect.setAttribute('height', `${frameHeight}`);
    frameRect.setAttribute('fill', config.frameColor);
    
    if (config.frameStyle === 'rounded') {
      frameRect.setAttribute('rx', '0'); // Bottom corners only rounded, but SVG rx rounds all corners.
      // So use a path instead.
      const path = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
      const pathString = getRoundedFramePathString(width, frameHeight, radius);
      path.setAttribute('d', pathString);
      path.setAttribute('fill', config.frameColor);
      svgNode.appendChild(path);
    } else {
      svgNode.appendChild(frameRect);
    }

    if (config.frameLabel) {
      const textNode = doc.createElementNS('http://www.w3.org/2000/svg', 'text');
      textNode.setAttribute('x', `${width / 2}`);
      textNode.setAttribute('y', `${width + frameHeight / 2}`);
      textNode.setAttribute('fill', config.frameLabelColor);
      textNode.setAttribute('font-family', FONT_FAMILY);
      textNode.setAttribute('font-size', `${fontSize}px`);
      textNode.setAttribute('font-weight', '600');
      textNode.setAttribute('text-anchor', 'middle');
      textNode.setAttribute('dominant-baseline', 'central');
      textNode.textContent = config.frameLabel; 
      svgNode.appendChild(textNode);
    }
  }

  const serializer = new XMLSerializer();
  const contentStr = serializer.serializeToString(svgNode);
  const blob = new Blob([contentStr], { type: 'image/svg+xml' });
  triggerDownload(blob, filename);
};

export const exportPDF = async (content: string, config: QRConfig, logoFile: string | null, filename: string) => {
  const qr = createQRGenerator(2048, content, config, logoFile, true);
  // jsPDF A4 is 210 x 297 mm
  // For PDF, background must not be transparent. So force solid white.
  const pdfConfig = { ...config, backgroundTransparent: false };
  if (pdfConfig.backgroundColor === 'transparent' || pdfConfig.backgroundColor === '') {
     pdfConfig.backgroundColor = '#FFFFFF';
  }
  const canvas = await composeCanvasWithFrame(qr, 2048, pdfConfig);
  const pngDataUrl = canvas.toDataURL('image/png');

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const imgWidth = 170; // 210 - 20 - 20 (margins)
  const imgHeight = (canvas.height / canvas.width) * imgWidth;
  const x = 20;
  const y = (297 / 3) - (imgHeight / 2); // Centered in top 2/3 roughly, per BRD: "centered in the top two-thirds of the page".
  // Top 2/3 means center Y is at 297/3 = 99mm.

  doc.addImage(pngDataUrl, 'PNG', x, Math.max(20, y), imgWidth, imgHeight);
  doc.save(filename);
};
